import { z } from 'zod';
import {
    ComplianceAutomationActionType,
    ComplianceAutomationActionTypeType,
    ComplianceAutomationType,
} from 'src/api_openapi/v2/compliance/automationSchemas';
import { NetDepositLimitConfigurationState } from 'src/components/DepositLimitTab/NetDepositLimit/NetDepositLimitConfigurationState';
import { GamstopPreviouslyPositiveState } from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.state';
import {
    GAMSTOP_PP_DEFAULT_EXECUTION_ORDERS,
    GamstopPpActionExecutionOrderKeyType,
    GamstopPpAutomatedActionType,
    GamstopPpCommunicationOnlyModeType,
    GamstopPpPrimaryActionType,
    gamstopPpSetAccountStatusActionSchema,
    getGamstopPpAutomatedActionOption,
    isPrimaryGamstopPpAction,
    isSendCommunicationOnlyAction,
} from 'src/components/ComplianceConfiguration/GamstopPreviouslyPositive/GamstopPreviouslyPositive.types';
import { putRollingLimits } from 'src_server/types/responsibleGambling';

export const GAMSTOP_PP_AUTOMATION_NAME = 'GAMSTOP_PREVIOUSLY_POSITIVE';

export const GAMSTOP_PP_AUTOMATION_DESCRIPTION =
    'Automated action applied when a Gamstop check returns Previously Positive.';

export const GAMSTOP_PP_AUTOMATION_SOURCE = 'automation';

const gamstopPpNetDepositLimitAutomationConfigSchema = z.object({
    'window-length-in-days': z.number(),
    'active-limit-value': z.number(),
    'limit-value-max': z.number().nullable(),
    'triggers-warning-email': z.boolean(),
    'hard-limit': z.boolean(),
    'limit-type': putRollingLimits.limitTypeSchema,
    'calculation-type': putRollingLimits.calculationTypeSchema.optional(),
});

export type GamstopPpNetDepositLimitAutomationConfigType = z.infer<
    typeof gamstopPpNetDepositLimitAutomationConfigSchema
>;

const gamstopPpApplyRestrictionConfigSchema = z.object({
    'restriction-type': z.literal('net-deposit-limit'),
    'age-restricted': z.boolean(),
    limit: gamstopPpNetDepositLimitAutomationConfigSchema.optional(),
    'over-25': gamstopPpNetDepositLimitAutomationConfigSchema.optional(),
    'under-25': gamstopPpNetDepositLimitAutomationConfigSchema.optional(),
});

export type GamstopPpApplyRestrictionConfigType = z.infer<typeof gamstopPpApplyRestrictionConfigSchema>;

const gamstopPpSetAccountStatusConfigSchema = z.object({
    'status-to-set': gamstopPpSetAccountStatusActionSchema,
    description: z.string().optional(),
    source: z.string().optional(),
    'suspension-duration': z.number().positive().optional(),
    //spelling on backend is wrong, dont change it
    permament: z.boolean().optional(),
});

type GamstopPpSetAccountStatusConfigType = z.infer<typeof gamstopPpSetAccountStatusConfigSchema>;

const parseWithSchema = <T>(schema: z.ZodType<T>, value: unknown): T | null => {
    const parsed = schema.safeParse(value);

    return parsed.success ? parsed.data : null;
};

const parseComplianceRestrictionPeriod = (period: string): number => {
    const parsed = parseInt(period, 10);

    if (parsed <= 0) {
        return 7;
    }

    return parsed;
};

const mapActionType = (action: GamstopPpPrimaryActionType): ComplianceAutomationActionTypeType => {
    if (action === 'apply-custom-limit') {
        return 'SetDepositLimit';
    }

    return 'SetAccountStatus';
};

const isCommunicationModeEmailEnabled = (mode: GamstopPpCommunicationOnlyModeType): boolean => {
    return mode === 'send-email-only' || mode === 'send-email-and-popup';
};

const isCommunicationModePopupEnabled = (mode: GamstopPpCommunicationOnlyModeType): boolean => {
    return mode === 'send-popup-only' || mode === 'send-email-and-popup';
};

const buildSendEmailEnabled = (state: GamstopPreviouslyPositiveState): boolean => {
    if (isSendCommunicationOnlyAction(state.selectedAction)) {
        return isCommunicationModeEmailEnabled(state.communicationOnlyMode);
    }

    return isPrimaryGamstopPpAction(state.selectedAction) && state.sendEmailEnabled;
};

const buildShowPopupEnabled = (state: GamstopPreviouslyPositiveState): boolean => {
    if (isSendCommunicationOnlyAction(state.selectedAction)) {
        return isCommunicationModePopupEnabled(state.communicationOnlyMode);
    }

    return isPrimaryGamstopPpAction(state.selectedAction) && state.sendPopupEnabled;
};

const GAMSTOP_PP_POPUP_NAME = 'gamstop-previously-positive';
const GAMSTOP_PP_EMAIL_TEMPLATE_ID = 'gamstop-previously-positive';

const buildShowPopupConfig = (): Record<string, unknown> => ({
    'popup-name': GAMSTOP_PP_POPUP_NAME,
});

const buildSendEmailConfig = (): Record<string, unknown> => ({
    'template-id': GAMSTOP_PP_EMAIL_TEMPLATE_ID,
});

const buildAutomationAction = (
    state: GamstopPreviouslyPositiveState,
    orderKey: GamstopPpActionExecutionOrderKeyType,
    enabled: boolean,
    actionType: ComplianceAutomationActionTypeType,
    config: Record<string, unknown>
): ComplianceAutomationActionType => {
    return {
        executionOrder: state.getActionExecutionOrder(orderKey) ?? GAMSTOP_PP_DEFAULT_EXECUTION_ORDERS[orderKey],
        enabled,
        actionType,
        config,
    };
};

const resolveActionOrderKey = (action: ComplianceAutomationActionType): GamstopPpActionExecutionOrderKeyType | null => {
    if (action.actionType === 'SetDepositLimit') {
        return 'apply-custom-limit';
    }

    if (action.actionType === 'SendEmail') {
        return 'SendEmail';
    }

    if (action.actionType === 'ShowPopup') {
        return 'ShowPopup';
    }

    if (action.actionType === 'SetAccountStatus') {
        const statusToSet = readStatusToSet(action.config);

        return isPrimaryGamstopPpAction(statusToSet) ? statusToSet : null;
    }

    return null;
};

const hydrateActionExecutionOrders = (
    state: GamstopPreviouslyPositiveState,
    automation: ComplianceAutomationType
): void => {
    const orders: Partial<Record<GamstopPpActionExecutionOrderKeyType, number>> = {};

    for (const action of automation.actions) {
        const orderKey = resolveActionOrderKey(action);

        if (orderKey === null || action.executionOrder === undefined) {
            continue;
        }

        orders[orderKey] = action.executionOrder;
    }

    state.setActionExecutionOrders(orders);
};

const buildPrimaryActions = (state: GamstopPreviouslyPositiveState): ComplianceAutomationType['actions'] => {
    if (!isPrimaryGamstopPpAction(state.selectedAction)) {
        return [];
    }

    return [
        buildAutomationAction(
            state,
            state.selectedAction,
            true,
            mapActionType(state.selectedAction),
            buildActionConfig(state.selectedAction, state)
        ),
    ];
};

const buildCommunicationActions = (state: GamstopPreviouslyPositiveState): ComplianceAutomationType['actions'] => {
    const actions: ComplianceAutomationType['actions'] = [];

    if (buildSendEmailEnabled(state)) {
        actions.push(buildAutomationAction(state, 'SendEmail', true, 'SendEmail', buildSendEmailConfig()));
    }

    if (buildShowPopupEnabled(state)) {
        actions.push(buildAutomationAction(state, 'ShowPopup', true, 'ShowPopup', buildShowPopupConfig()));
    }

    return actions;
};

const mapLimitFormValueToAutomationConfig = (
    limit: putRollingLimits.LimitObjectType
): GamstopPpNetDepositLimitAutomationConfigType => ({
    'window-length-in-days': limit.windowLengthInDays,
    'active-limit-value': limit.pendingLimitValue,
    'limit-value-max': limit.limitValueMax,
    'triggers-warning-email': limit.triggersWarningEmail,
    'hard-limit': limit.hardLimit,
    'limit-type': limit.limitType,
    'calculation-type': limit.calculationType,
});

const mapAutomationLimitConfigToFormValue = (
    limit: GamstopPpNetDepositLimitAutomationConfigType
): putRollingLimits.LimitObjectType => ({
    windowLengthInDays: limit['window-length-in-days'],
    pendingLimitValue: limit['active-limit-value'],
    limitValueMax: limit['limit-value-max'],
    triggersWarningEmail: limit['triggers-warning-email'],
    hardLimit: limit['hard-limit'],
    limitType: limit['limit-type'],
    calculationType: limit['calculation-type'] ?? 'net',
});

const getActionDescription = (action: GamstopPpAutomatedActionType): string => {
    return getGamstopPpAutomatedActionOption(action)?.description ?? GAMSTOP_PP_AUTOMATION_DESCRIPTION;
};

const buildSetAccountStatusConfig = (
    action: Exclude<GamstopPpPrimaryActionType, 'apply-custom-limit'>,
    state: GamstopPreviouslyPositiveState
): GamstopPpSetAccountStatusConfigType => {
    const baseConfig: GamstopPpSetAccountStatusConfigType = {
        'status-to-set': action,
        description: getActionDescription(action),
        source: GAMSTOP_PP_AUTOMATION_SOURCE,
    };

    if (action === 'compliance-restriction') {
        return {
            ...baseConfig,
            'suspension-duration': parseComplianceRestrictionPeriod(state.complianceRestrictionPeriod),
        };
    }

    if (action === 'deposit-restriction') {
        const suspensionDuration = state.depositRestrictionPeriod;

        if (suspensionDuration === null) {
            return {
                ...baseConfig,
                //spelling on backend is wrong, dont change it
                permament: true,
            };
        }

        return {
            ...baseConfig,
            //spelling on backend is wrong, dont change it
            permament: false,
            'suspension-duration': suspensionDuration,
        };
    }

    return baseConfig;
};

const buildApplyRestrictionConfig = (
    state: GamstopPreviouslyPositiveState
): GamstopPpApplyRestrictionConfigType | null => {
    const limitConfigurationOver25 = state.limitConfigurationState.getFormValue();

    if (limitConfigurationOver25 === null) {
        return null;
    }

    if (state.isAgeRestrictedLimitsEnabled) {
        const limitConfigurationUnder25 = state.limitConfigurationStateUnder25.getFormValue();

        if (limitConfigurationUnder25 === null) {
            return null;
        }

        return {
            'restriction-type': 'net-deposit-limit',
            'age-restricted': true,
            'over-25': mapLimitFormValueToAutomationConfig(limitConfigurationOver25),
            'under-25': mapLimitFormValueToAutomationConfig(limitConfigurationUnder25),
        };
    }

    return {
        'restriction-type': 'net-deposit-limit',
        'age-restricted': false,
        limit: mapLimitFormValueToAutomationConfig(limitConfigurationOver25),
    };
};

const buildActionConfig = (
    action: GamstopPpPrimaryActionType,
    state: GamstopPreviouslyPositiveState
): Record<string, unknown> => {
    switch (action) {
        case 'apply-custom-limit': {
            const config = buildApplyRestrictionConfig(state);

            if (config === null) {
                return {
                    'restriction-type': 'net-deposit-limit',
                    'age-restricted': false,
                };
            }

            return config;
        }
        case 'blocked':
        case 'suspended':
        case 'deposit-restriction':
        case 'compliance-restriction':
            return buildSetAccountStatusConfig(action, state);
    }
};

export const buildGamstopPpAutomationDto = (state: GamstopPreviouslyPositiveState): ComplianceAutomationType | null => {
    if (state.selectedAction === null) {
        return null;
    }

    if (state.isCustomLimitSelected && buildApplyRestrictionConfig(state) === null) {
        return null;
    }

    return {
        name: GAMSTOP_PP_AUTOMATION_NAME,
        description: GAMSTOP_PP_AUTOMATION_DESCRIPTION,
        enabled: !state.isNoActionSelected,
        actions: [...buildPrimaryActions(state), ...buildCommunicationActions(state)],
    };
};

const readStatusToSet = (config: unknown): GamstopPpAutomatedActionType | null => {
    const parsedConfig = parseWithSchema(gamstopPpSetAccountStatusConfigSchema, config);

    return parsedConfig?.['status-to-set'] ?? null;
};

const hydrateLimitConfigurationState = (
    limitConfigurationState: NetDepositLimitConfigurationState,
    limit: putRollingLimits.LimitObjectType
): void => {
    limitConfigurationState.loadFromLimitValue(limit);
};

const hydrateApplyRestrictionConfig = (state: GamstopPreviouslyPositiveState, config: unknown): void => {
    const parsedConfig = parseWithSchema(gamstopPpApplyRestrictionConfigSchema, config);

    if (parsedConfig === null) {
        return;
    }

    state.disableAgeRestrictedLimits();

    if (parsedConfig['age-restricted']) {
        if (parsedConfig['over-25'] !== undefined) {
            hydrateLimitConfigurationState(
                state.limitConfigurationState,
                mapAutomationLimitConfigToFormValue(parsedConfig['over-25'])
            );
        }

        if (parsedConfig['under-25'] !== undefined) {
            state.enableAgeRestrictedLimits();
            hydrateLimitConfigurationState(
                state.limitConfigurationStateUnder25,
                mapAutomationLimitConfigToFormValue(parsedConfig['under-25'])
            );
        }

        return;
    }

    if (parsedConfig.limit !== undefined) {
        hydrateLimitConfigurationState(
            state.limitConfigurationState,
            mapAutomationLimitConfigToFormValue(parsedConfig.limit)
        );
    }
};

const hydrateSetAccountStatusConfig = (state: GamstopPreviouslyPositiveState, config: unknown): void => {
    const parsedConfig = parseWithSchema(gamstopPpSetAccountStatusConfigSchema, config);

    if (parsedConfig === null) {
        return;
    }

    if (
        parsedConfig['status-to-set'] === 'compliance-restriction' &&
        parsedConfig['suspension-duration'] !== undefined
    ) {
        state.onUpdateComplianceRestrictionPeriod(String(parsedConfig['suspension-duration']));
    }

    if (parsedConfig['status-to-set'] === 'deposit-restriction') {
        if (parsedConfig.permament === true) {
            state.onUpdateDepositRestrictionPeriod(null);
            return;
        }

        if (parsedConfig['suspension-duration'] !== undefined) {
            state.onUpdateDepositRestrictionPeriod(parsedConfig['suspension-duration']);
        }
    }
};

export const hydrateGamstopPpStateFromAutomation = (
    state: GamstopPreviouslyPositiveState,
    automation: ComplianceAutomationType
): void => {
    hydrateActionExecutionOrders(state, automation);

    for (const action of automation.actions) {
        if (action.actionType === 'SetAccountStatus') {
            hydrateSetAccountStatusConfig(state, action.config);
        }
    }

    const enabledActions = automation.actions.filter((action) => action.enabled);
    const enabledPrimaryAction = enabledActions.find(
        (action) => action.actionType === 'SetAccountStatus' || action.actionType === 'SetDepositLimit'
    );
    const isSendEmailEnabled = enabledActions.some((action) => action.actionType === 'SendEmail');
    const isShowPopupEnabled = enabledActions.some((action) => action.actionType === 'ShowPopup');

    state.setSendEmailEnabled(false);
    state.setSendPopupEnabled(false);

    if (!automation.enabled) {
        state.setSelectedAction('no-action');
        return;
    }

    if (enabledPrimaryAction !== undefined) {
        if (enabledPrimaryAction.actionType === 'SetDepositLimit') {
            state.setSelectedAction('apply-custom-limit');
            hydrateApplyRestrictionConfig(state, enabledPrimaryAction.config);
        } else {
            state.setSelectedAction(readStatusToSet(enabledPrimaryAction.config));
        }

        state.setSendEmailEnabled(isSendEmailEnabled);
        state.setSendPopupEnabled(isShowPopupEnabled);

        return;
    }

    if (isSendEmailEnabled || isShowPopupEnabled) {
        state.setSelectedAction('send-communication-only');

        if (isSendEmailEnabled && isShowPopupEnabled) {
            state.setCommunicationOnlyMode('send-email-and-popup');
            return;
        }

        if (isSendEmailEnabled) {
            state.setCommunicationOnlyMode('send-email-only');
            return;
        }

        state.setCommunicationOnlyMode('send-popup-only');
        return;
    }

    state.setSelectedAction('no-action');
};
